// AB#1870 — 3-state segmented radio for category gender targeting.
//
// Maps 1:1 to `CategoryTargetGender { NoPreference=0, Female=1, Male=2 }`.
// Default = No preference (start/leading). Solid surfaces only (no gradients);
// selected pill = solid #6138e0 / white, unselected = #f1f5f9 / #475569 (AA).
//
// A11y: `role="radiogroup"` + `role="radio" aria-checked`; arrow-key navigable
// (Left/Up = previous, Right/Down = next, wrapping). RTL: flex row honours
// `dir` so "No preference" stays leading; arrow keys follow visual order via
// logical first/last. Used in create + edit (CreateCategoryModal).

import React, { useRef } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { CategoryTargetGender } from '../../../types/api-responses';
import styles from './CategoryModals.module.css';

interface GenderTargetControlProps {
    value: CategoryTargetGender;
    onChange: (value: CategoryTargetGender) => void;
    /** id of the group label element, wired via aria-labelledby. */
    labelId: string;
}

const ORDER: CategoryTargetGender[] = [
    CategoryTargetGender.NoPreference,
    CategoryTargetGender.Female,
    CategoryTargetGender.Male,
];

const LABEL_KEY: Record<CategoryTargetGender, string> = {
    [CategoryTargetGender.NoPreference]: 'superadmin.categories.gender.none',
    [CategoryTargetGender.Female]: 'superadmin.categories.gender.female',
    [CategoryTargetGender.Male]: 'superadmin.categories.gender.male',
};

const GenderTargetControl: React.FC<GenderTargetControlProps> = ({
    value,
    onChange,
    labelId,
}) => {
    const { t } = useLanguage();
    const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const focusIndex = (idx: number) => {
        const wrapped = (idx + ORDER.length) % ORDER.length;
        const next = ORDER[wrapped];
        onChange(next);
        btnRefs.current[wrapped]?.focus();
    };

    const onKeyDown = (e: React.KeyboardEvent, currentIdx: number) => {
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                focusIndex(currentIdx + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                focusIndex(currentIdx - 1);
                break;
            default:
                break;
        }
    };

    return (
        <div
            className={styles.genderGroup}
            role="radiogroup"
            aria-labelledby={labelId}
        >
            {ORDER.map((option, idx) => {
                const selected = value === option;
                return (
                    <button
                        key={option}
                        type="button"
                        ref={(el) => {
                            btnRefs.current[idx] = el;
                        }}
                        role="radio"
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        className={`${styles.genderOption} ${selected ? styles.genderOptionSelected : ''}`}
                        onClick={() => onChange(option)}
                        onKeyDown={(e) => onKeyDown(e, idx)}
                    >
                        {t(LABEL_KEY[option])}
                    </button>
                );
            })}
        </div>
    );
};

export default GenderTargetControl;
